import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Check, Container, MapPin, Palette } from 'lucide-react';

// Predefined color palette
const COLOR_PALETTE = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
  '#fca5a5', '#fdba74', '#fde047', '#86efac', '#5eead4', '#93c5fd', '#c4b5fd', '#f9a8d4',
  '#ffffff', '#d1d5db', '#9ca3af', '#6b7280', '#4b5563', '#374151', '#1f2937', '#000000',
];

export const CellSelectColorPicker = ({ 
  currentColor, 
  currentValue,
  options = [],
  onColorChange, 
  onValueChange,
  children,
  disabled = false,
  containerNumber = null,
  fieldLabel = 'Select',
  placeholder = 'Select...',
  valueKey = 'id',
  labelKey = 'name'
}) => {
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedValue(currentValue || '');
  }, [currentValue, open]);

  const handleColorSelect = (color) => {
    onColorChange(color);
  };

  const handleClearColor = () => {
    onColorChange(null);
  };

  // Auto-save when selection changes
  const handleValueChange = async (newValue) => {
    setSelectedValue(newValue);
    if (newValue && newValue !== 'none') {
      setSaving(true);
      try {
        await onValueChange(newValue);
        setOpen(false);
      } catch (e) {
        console.error('Failed to save:', e);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onValueChange(selectedValue || null);
      setOpen(false);
    } catch (e) {
      console.error('Failed to save:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      setSelectedValue('');
      await onValueChange(null);
      setOpen(false);
    } catch (e) {
      console.error('Failed to clear:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleClick = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  const handleTouchStart = (e) => {
    if (disabled) return;
    const timer = setTimeout(() => {
      setOpen(true);
    }, 400);
    e.target.dataset.longPressTimer = timer;
  };

  const handleTouchEnd = (e) => {
    if (e.target.dataset.longPressTimer) {
      clearTimeout(parseInt(e.target.dataset.longPressTimer));
    }
  };

  const getCurrentLabel = () => {
    const option = options.find(o => o[valueKey] === currentValue);
    return option ? option[labelKey] : null;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <div 
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          className="w-full h-full min-h-[28px] flex items-center cursor-pointer"
          data-testid="cell-select-picker-trigger"
        >
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[300px] p-4 bg-slate-900 border-slate-700" 
        align="start"
        sideOffset={5}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-4">
          {/* Container Number Header */}
          {containerNumber && (
            <div className="flex items-center gap-2 pb-3 border-b border-slate-700">
              <Container className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <span className="text-base font-mono font-semibold text-blue-400 truncate">
                {containerNumber}
              </span>
            </div>
          )}
          
          {/* Select Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-green-400" />
              <Label className="text-sm font-medium text-white">{fieldLabel}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={selectedValue || "none"}
                onValueChange={(v) => handleValueChange(v === "none" ? "" : v)}
              >
                <SelectTrigger className="flex-1 h-10 bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 max-h-[200px]">
                  <SelectItem value="none" className="text-slate-400">None</SelectItem>
                  {options.map(option => (
                    <SelectItem 
                      key={option[valueKey]} 
                      value={option[valueKey]} 
                      className="text-white"
                    >
                      {option[labelKey]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="h-10 px-4 bg-green-600 hover:bg-green-500"
              >
                {saving ? (
                  <span className="animate-spin">↻</span>
                ) : (
                  <Check className="h-4 w-4" />
                )}
              </Button>
              {currentValue && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleClear}
                  disabled={saving}
                  className="h-10 px-2 text-slate-400 hover:text-white"
                  title="Clear selection"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          
          <Separator className="bg-slate-700" />
          
          {/* Color Picker Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-pink-400" />
                <Label className="text-sm font-medium text-white">Text Color</Label>
              </div>
              {currentColor && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearColor}
                  className="text-slate-400 hover:text-white h-7 px-2"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-8 gap-1.5">
              {COLOR_PALETTE.map((color) => (
                <button
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  className={`w-7 h-7 rounded-md border transition-transform hover:scale-110 active:scale-95 flex items-center justify-center touch-manipulation ${
                    currentColor === color 
                      ? 'border-white ring-2 ring-blue-500' 
                      : 'border-slate-600 hover:border-slate-400'
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                >
                  {currentColor === color && (
                    <Check className={`h-3 w-3 ${color === '#ffffff' || color === '#fde047' || color === '#86efac' ? 'text-black' : 'text-white'}`} />
                  )}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-2">
              <input
                type="color"
                value={currentColor || '#3b82f6'}
                onChange={(e) => handleColorSelect(e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent touch-manipulation"
                title="Custom color"
              />
              <span className="text-xs text-slate-400">Custom color</span>
            </div>
          </div>
          
          <p className="text-[10px] text-slate-500 text-center pt-2 border-t border-slate-800">
            Click cell to edit • Colors save automatically
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CellSelectColorPicker;
